import { readCorrelationId } from './correlationId';

export type RequestErrorKind = 'business' | 'server' | 'malformed' | 'network' | 'client';

export interface ClassifiedRequestError {
  kind: RequestErrorKind;
  message?: string;
  code?: string;
  status?: number;
  axiosCode?: string;
  method?: string;
  url?: string;
  correlationId?: string;
  rawBody?: string;
}

const RAW_BODY_MAX_LENGTH = 500;

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object';

const toRawBody = (body: unknown): string | undefined => {
  if (body === undefined) return undefined;
  let text: string;
  if (typeof body === 'string') {
    text = body;
  } else {
    try {
      text = JSON.stringify(body) ?? String(body);
    } catch {
      text = String(body);
    }
  }
  return text.length > RAW_BODY_MAX_LENGTH ? `${text.slice(0, RAW_BODY_MAX_LENGTH)}…(truncado)` : text;
};

interface AxiosLikeError {
  isAxiosError?: boolean;
  code?: string;
  message?: string;
  config?: { method?: string; url?: string; headers?: unknown };
  response?: { status?: number; data?: unknown };
}

const SESSION_EXPIRED_MESSAGE = 'Session expired';

export const isUnauthorizedOrExpiredError = (err: unknown): boolean => {
  if (err instanceof Error && err.message === SESSION_EXPIRED_MESSAGE) {
    return true;
  }

  if (isObject(err)) {
    const axiosLike = err as AxiosLikeError & { status?: number };
    const status = axiosLike.response?.status ?? axiosLike.status;
    if (status === 401) return true;

    const message = typeof axiosLike.message === 'string' ? axiosLike.message : '';
    if (message === SESSION_EXPIRED_MESSAGE || /status code 401/i.test(message)) {
      return true;
    }
  }

  return classifyRequestError(err).status === 401;
};

export const classifyRequestError = (err: unknown): ClassifiedRequestError => {
  if (!isObject(err)) {
    return { kind: 'client', message: typeof err === 'string' ? err : undefined };
  }

  const axiosError = err as AxiosLikeError;

  const isAxiosLike = axiosError.isAxiosError === true || isObject(axiosError.response);
  if (!isAxiosLike) {
    return { kind: 'client', message: axiosError.message };
  }

  const method = axiosError.config?.method?.toUpperCase();
  const url = axiosError.config?.url;
  const correlationId = readCorrelationId(axiosError.config?.headers);
  const base = { axiosCode: axiosError.code, method, url, correlationId };

  if (!axiosError.response) {
    return { ...base, kind: 'network', message: axiosError.message };
  }

  const { status, data } = axiosError.response;
  const withResponse = { ...base, status };

  if (!isObject(data)) {
    return { ...withResponse, kind: 'malformed', rawBody: toRawBody(data) };
  }

  const errors = (data as { errors?: unknown }).errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const first = (errors[0] ?? {}) as { code?: string; message?: string };
    return { ...withResponse, kind: 'business', code: first.code, message: first.message };
  }

  const serverError = (data as { error?: { code?: string; message?: string } }).error;
  if (serverError && (serverError.code || serverError.message)) {
    const meta = (data as { meta?: { correlationId?: string } }).meta;
    return {
      ...withResponse,
      kind: 'server',
      code: serverError.code,
      message: serverError.message,
      correlationId: correlationId ?? meta?.correlationId,
    };
  }

  return { ...withResponse, kind: 'malformed', rawBody: toRawBody(data) };
};

export const classifySuccessBody = (
  body: unknown,
  expect: 'array' | 'object'
): ClassifiedRequestError | null => {
  if (!isObject(body)) {
    return { kind: 'malformed', status: 200, rawBody: toRawBody(body) };
  }

  const payload = (body as { data?: unknown }).data;
  const isValid = expect === 'array' ? Array.isArray(payload) && payload.length > 0 : isObject(payload);

  return isValid ? null : { kind: 'malformed', status: 200, rawBody: toRawBody(body) };
};

export const logRequestError = (scope: string, c: ClassifiedRequestError, raw?: unknown): void => {
  console.error(`[${scope}] request failed`, {
    kind: c.kind,
    status: c.status,
    code: c.code,
    axiosCode: c.axiosCode,
    method: c.method,
    url: c.url,
    correlationId: c.correlationId,
    message: c.message,
    rawBody: c.rawBody,
  });

  if (c.kind === 'client' && raw !== undefined) {
    console.error(`[${scope}] stack`, raw);
  }
};

const MESSAGES_BY_KIND: Record<RequestErrorKind, string> = {
  business: 'No se pudo completar la operación.',
  server: 'El servidor devolvió un error inesperado. Intente nuevamente.',
  malformed: 'La respuesta del servidor no pudo interpretarse. Intente nuevamente.',
  network: 'No se obtuvo respuesta del servidor. Revise su conexión e intente nuevamente.',
  client: 'Ocurrió un error inesperado en la aplicación. Recargue la página.',
};

export const defaultMessageForKind = (kind: RequestErrorKind): string => MESSAGES_BY_KIND[kind];
